import { useContext } from 'react';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import ivclass from '@invideoio/web-shared/Modules/IVClass';

import AppLayout from '@layouts/AppLayout';
import { BlogTypeDetailsType, CategoryType } from 'src/types';

import { fetchAllCategories, fetchCategoryDetails } from '@lib/API/categoryAPI';
import { StateContext } from '@store/store';
import HeaderWithCTA from '@components/header-with-cta';
import CategoryHeaderSection from '@components/category-header-section';
import Breadcrumb from '@components/breadcrumbs';
import { generateBreadcrumbsFromUrl } from '@utils/common.utils';
import PaginatedBlogList from '@components/paginated-blog-listing';
import SubHeading from '@components/sub-heading';
import ItemsTray from '@components/items-tray';
import CategoryCard from '@components/categories/category-card';
import BlogSEO from '@components/SEO/BlogSEO';
import SeeAllLink from '@components/see-all-link';
import BlogPostCard from '@components/blog-posts/blog-post-card';
import useBlogAppLoad from '@hooks/useBlogAppLoad';

interface categoryType {
  categoryDetails: BlogTypeDetailsType;
  otherCategories: CategoryType[];
}

function CategoryBlogPage({ categoryDetails, otherCategories }: categoryType) {
  const globalState = useContext(StateContext);
  const { platform, browser } = globalState;
  const router = useRouter();

  const pathName = `/blog/category/${router.query.category}/`;
  const { trendingPosts, name, slug } = categoryDetails;

  useBlogAppLoad({ platform });

  return (
    <AppLayout platform={platform}>
      <BlogSEO seo={categoryDetails?.SEO} url={pathName} />
      <HeaderWithCTA />
      <CategoryHeaderSection
        platform={platform}
        browser={browser}
        {...categoryDetails}
      />
      {trendingPosts && trendingPosts.length > 0 && (
        <section className={ivclass('iv-bg-grey-7 iv-overflow-x-hidden')}>
          <div className={ivclass('iv-grid iv-grid-cols-12 iv-col-span-12')}>
            <div className={ivclass('iv-col-start-1 iv-col-span-12')}>
              <div
                className={ivclass(
                  'iv-px-[120px] lg:iv-px-[73px] md:iv-px-25 sm:iv-px-20',
                  'iv-mt-[65px] lg:iv-mt-[70px] md:iv-mt-[57px] sm:iv-mt-[43px] ',
                  'iv-mb-[85px] lg:iv-mb-80 md:iv-mb-50 sm:iv-mb-25',
                )}
              >
                <div
                  className={ivclass(
                    'iv-pl-[56px] lg:iv-pl-40 md:iv-pl-[32px] sm:iv-pl-25',
                    'iv-mb-25 lg:iv-mb-[28px] md:iv-mb-[11px] sm:iv-mb-10',
                  )}
                >
                  <Breadcrumb
                    breadcrumbItems={generateBreadcrumbsFromUrl(
                      pathName.replace(/\/?$/, ''),
                    )}
                  />
                </div>

                <div
                  className={ivclass(
                    'iv-container xl:iv-overflow-x-hidden',
                    'iv-mx-auto',
                  )}
                >
                  <div className={ivclass('xl:iv-overflow-x-hidden')}>
                    <div
                      className={ivclass(
                        'iv-grid iv-grid-cols-12 iv-col-span-12',
                      )}
                    >
                      <div className={ivclass('iv-col-span-12')}>
                        <div
                          className={ivclass(
                            'iv-flex iv-justify-between iv-items-baseline',
                            'iv-mb-[67px] md:iv-mb-[32px] sm:iv-mb-15',
                            'iv-text-body-2xl iv-font-semibold ',
                          )}
                        >
                          <div
                            className={ivclass(
                              'iv-text-heading-2xl md:iv-text-heading-m sm:iv-text-[36px]',
                              'sm:iv-leading-[42px]',
                              'iv-font-extrabold',
                              'iv-pl-[56px] lg:iv-pl-[35px] md:iv-pl-[32px] sm:iv-pl-25',
                              'sm:iv-max-w-[280px]',
                            )}
                          >
                            {'Top trending posts 🔥'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={ivclass('sm:iv-mt-[7px]')}>
                      {trendingPosts && (
                        <ItemsTray
                          platform={platform}
                          sliderWrapperCls={ivclass(
                            'lg:iv-mr-20',
                            'md:iv-mr-[12px]',
                            'sm:iv-mr-20',
                          )}
                        >
                          {trendingPosts?.map(post => (
                            <div
                              key={post.id}
                              className={ivclass(
                                'iv-relative',
                                'iv-mr-40 md:iv-mr-25',
                              )}
                            >
                              <BlogPostCard
                                {...post}
                                devicePlatform={platform}
                                browser={browser}
                                backgroundColorClass={'iv-bg-grey-0'}
                              />
                            </div>
                          ))}
                        </ItemsTray>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section
        className={ivclass(
          'iv-mb-100 lg:iv-mb-100 md:iv-mb-[80px] sm:iv-mb-50',
        )}
      >
        <div className={ivclass('iv-grid iv-grid-cols-12 iv-col-span-12')}>
          <div className={ivclass('iv-col-start-1 iv-col-span-12')}>
            <PaginatedBlogList
              listTypeKey={'categories_v_2'}
              listName={name.toLowerCase()}
              slug={slug}
              browserURL={pathName}
              trendingPosts={trendingPosts}
              browser={browser}
              devicePlatform={platform}
            />
          </div>
        </div>
      </section>
      <section
        className={ivclass(
          'iv-bg-black-4',
          'iv-pt-[90px]',
          'iv-pb-[105px] lg:iv-pb-[100px] md:iv-pb-[75px] sm:iv-pb-60',
        )}
      >
        <div
          className={ivclass('iv-container iv-mx-auto ', 'iv-overflow-hidden')}
        >
          <div className={ivclass('iv-grid iv-grid-cols-12')}>
            <div
              className={ivclass('iv-col-span-12', 'md:iv-mx-15 sm:iv-mx-20')}
            >
              <SubHeading
                title={'Other categories'}
                viewAllURL={'/blog/category/'}
              />
              {otherCategories.length > 0 && (
                <div className={ivclass('lg:iv-mx-25 md:iv-mx-25 sm:iv-mx-25')}>
                  <ItemsTray
                    platform={platform}
                    sliderWrapperCls={ivclass(
                      'lg:iv-mr-15',
                      'md:iv-mr-15',
                      'sm:iv-mr-15',
                    )}
                  >
                    {otherCategories?.map(category => (
                      <div
                        key={category.id}
                        className={ivclass('iv-relative', 'iv-mr-30')}
                      >
                        <CategoryCard
                          browser={browser}
                          platform={platform}
                          {...category}
                        />
                      </div>
                    ))}
                  </ItemsTray>
                  <div
                    className={ivclass(
                      'xl:iv-hidden lg:iv-hidden md:iv-hidden ',
                      'iv-flex iv-justify-center',
                      'sm:iv-mt-40',
                    )}
                  >
                    <SeeAllLink viewAllURL={'/blog/category/'} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </AppLayout>
  );
}

export default CategoryBlogPage;

export const getServerSideProps: GetServerSideProps<
  categoryType
> = async context => {
  const { category } = context.query;
  const lang = context.locale;
  try {
    if (typeof category === 'string') {
      const categoryDetailsPromise = fetchCategoryDetails(category, lang);

      const otherCategoriesPromise = fetchAllCategories({
        listTypeToSkip: category,
        language: lang,
      });

      const [categoryDetails, otherCategories] = await Promise.all([
        categoryDetailsPromise,
        otherCategoriesPromise,
      ]);

      return {
        props: { categoryDetails, otherCategories },
      };
    }
    throw new Error(`Slug missing: ${category}`);
  } catch (error: any) {
    const status = error?.response?.status;

    console.log(status);
    if (status && status === 404) {
      return {
        notFound: true,
      };
    }
    console.log(`Error: `, error?.message);
    return {
      props: {
        categoryDetails: {
          slug: category as string,
          id: 0,
          name: '',
          description: '',
          trendingPosts: [],
          image: {
            url: '',
          },
          bgColor: '',
          SEO: {
            id: 0,
            Title: '',
            Description: '',
            CanonicalURL: '',
            Hreflang: [],
            Keywords: '',
            RobotFollow: null,
            RobotIndex: null,
            Meta: [],
          },
        },
        otherCategories: [],
      },
    };
  }
};
